import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  Fragment,
} from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { HeroImage } from '../heroImage/HeroImage';
import { HeroCategory } from '../heroCategory/HeroCategory';
import { HeroLoading } from '../../loading/HeroLoading';
import { Button } from '../../button/Button';
import { device } from '../../../Theme';
import {
  imageFallback,
  cmsImageResize,
  formatUrl,
  titleTrunc,
  getWidth,
} from '../../../../helpers';
import heroBoxSetBrokenImageDark from '../../../../img/heroBoxSetBrokenImageDark.png';
import heroBoxSetBrokenImageLight from '../../../../img/heroBoxSetBrokenImageLight.png';
import heroBoxSetMissingImageDark from '../../../../img/heroBoxSetMissingImageDark.png';
import heroBoxSetMissingImageLight from '../../../../img/heroBoxSetMissingImageLight.png';
import { withRouter } from 'react-router-dom';
import { rgba } from 'polished';

export const HeroCard = ({
  auth,
  items,
  boxSetImage,
  genre,
  noImage,
  logo,
  logoAlt,
  loading = false,
  isCategory,
  strings,
  handleCancel,
  isCancelled,
  isProgramme,
  isAutoPlay,
  timer,
  programmeNext,
  recommended,
  details,
  history,
}) => {
  const theme = useTheme();
  const [hasBoxsetImage, setHasBoxsetImage] = useState([]);
  const [width, setWidth] = useState(window.innerWidth);
  const HeroCardButton = withRouter(({ label, size, type, url, onClick }) => (
    <Button
      label={label}
      size={size}
      type={type}
      link={url}
      onClick={onClick}
    />
  ));

  const progLinkUrl = () => {
    if (details) {
      return `/programme/${details.id}`;
    }
  };

  let progLink = progLinkUrl();
  if (isAutoPlay) {
    progLink += '?play';
  }

  const memoizedCmsImageResize = useMemo(() => {
    return (image, size) => {
      return cmsImageResize(image, size);
    };
  }, []);

  const handleAutoPlay = (link) => {
    if (programmeNext) {
      history.push(link);
    }
  };

  useLayoutEffect(() => {
    getWidth(setWidth);
  }, []);

  useEffect(() => {
    setHasBoxsetImage(
      items &&
        Object.keys(items)
          .slice(0, 1)
          .map((key) => items[key].primary_media).length < 1
    );
  }, [items]);

  const renderHeroCardInner = (item) => {
    const {
      primary_media,
      primary_media_portrait,
      title,
      subheading,
      subTitle,
      content,
      buttonText,
      entered_url,
    } = item;

    return (
      <>
        {primary_media && (
          <HeroImage
            image={
              items
                ? memoizedCmsImageResize(primary_media, '726x564')
                : boxSetImage
            }
            isProgramme={isProgramme}
          />
        )}
        <HeroCardInner
          isCategory={isCategory}
          isProgramme={isProgramme}
          recommended={recommended}
          noImage={noImage}
          noBoxsetImage={!primary_media_portrait}
        >
          {!noImage && primary_media_portrait && (
            <HeroCardImageContainer>
              <HeroCardImage
                src={memoizedCmsImageResize(primary_media_portrait, '200x300')}
                onError={(e) =>
                  imageFallback(
                    e,
                    theme.name === 'light'
                      ? heroBoxSetBrokenImageLight
                      : heroBoxSetBrokenImageDark
                  )
                }
                alt={title}
              />
            </HeroCardImageContainer>
          )}
          <HeroTitleGroup>
            {noImage && logo && <CategoryLogo src={logo} alt={logoAlt} />}
            {subheading ? (
              <Tag isProgramme={isProgramme}>{subheading}</Tag>
            ) : (
              <Tag isProgramme={isProgramme}>{strings.upNext}</Tag>
            )}
            {title && (
              <Title
                isProgramme={isProgramme}
                programmeNext={programmeNext}
                recommended={recommended}
              >
                {!isProgramme ? title : titleTrunc(width, title, true)}
              </Title>
            )}
            {genre && <HeroCategory category={genre} />}
          </HeroTitleGroup>
          <HeroCardContent>
            {content && (
              <Description isProgramme={isProgramme}>{content}</Description>
            )}
            {subTitle && (
              <Description isProgramme={isProgramme}>{subTitle}</Description>
            )}
            <HeroCardCtas isProgramme={isProgramme}>
              <HeroCardButton
                label={
                  timer === 0 || isCancelled || !isAutoPlay
                    ? `${buttonText ? buttonText : strings.play}`
                    : `${strings.playingIn} ${timer}${strings.seconds}`
                }
                size="large"
                type="primary"
                url={formatUrl(entered_url ? entered_url : progLink)}
                onClick={() => handleAutoPlay(progLink)}
              />
              {/* WIP to pick up again once API has been sorted */}
              {/* {auth && (
                <Button
                  label={
                    isInAddToList(
                      items[key].entered_url.split('/').pop(),
                      myList
                    )
                      ? strings.removeFromList
                      : strings.addToList
                  }
                  size="large"
                  type="tertiary"
                  onClick={(event) =>
                    toggleProgAddToList(
                      event,
                      items[key].entered_url.split('/').pop(),
                      isInAddToList(
                        items[key].entered_url.split('/').pop(),
                        myList
                      )
                    )
                  }
                />
              )}
              */}
              {isCancelled === false && isAutoPlay && (
                <HeroCardButtonContainer>
                  <Button
                    label={strings.cancel}
                    size="large"
                    type="tertiary"
                    onClick={handleCancel}
                  />
                </HeroCardButtonContainer>
              )}
            </HeroCardCtas>
          </HeroCardContent>
        </HeroCardInner>
      </>
    );
  };

  return (
    <>
      {loading ? (
        <HeroLoading isCategory={isCategory} />
      ) : (
        <HeroCardContainer
          auth={auth}
          boxSetImage={hasBoxsetImage}
          logo={logo}
          isProgramme={isProgramme}
          programmeNext={programmeNext}
          recommended={recommended}
        >
          {items && Object.keys(items).length > 0 && (
            <HeroCardContainerInner isCategory={isCategory}>
              {Object.keys(items)
                .slice(0, 1)
                .map((key) => (
                  <Fragment key={key}>
                    {renderHeroCardInner(items[key])}
                  </Fragment>
                ))}
            </HeroCardContainerInner>
          )}
          {items && Object.keys(items).length === 0 && noImage && logo && (
            <CategoryLogo src={logo} alt={logoAlt} />
          )}

          {details && (
            <HeroCardContainerInner isCategory={isCategory}>
              {renderHeroCardInner(details)}
            </HeroCardContainerInner>
          )}
        </HeroCardContainer>
      )}
    </>
  );
};

const HeroCardContainer = styled.div`
  ${(props) =>
    !props.auth &&
    `
      margin-top: -69px;
  `}
  padding-bottom: 24px;
  position: relative;

  ${(props) =>
    props.isProgramme &&
    `
    margin-top: 0;
    padding-bottom: 0;
    z-index: 1;
    min-height: 0 !important;
    flex-basis: 60%;
    &:only-child {
      flex-basis: initial;
      @media ${device.widest} {
        flex-basis: 100%;
      }
    }
  `};
  &:empty {
    min-height: auto;
  }
  @media screen and (min-width: 640px) {
    ${(props) =>
      props.isProgramme &&
      `
        flex-basis: 50%;
    `};
  }
  @media ${device.tablet} {
    display: flex;
    align-items: center;
    top: 24px;
    margin-top: 0;
    position: relative;
    ${(props) => props.isProgramme && `top: 0;`};
  }
  @media ${device.widest} {
    ${(props) =>
      props.programmeNext &&
      props.recommended &&
      `flex-grow: 1;
      align-items: flex-end;
    `};
    ${(props) => props.isProgramme && `flex-basis: initial;`};
  }
  ${(props) =>
    props.logo
      ? `
      min-height: auto;
    `
      : `
    @media ${device.tablet} {
      min-height: 330px;
    }
    @media ${device.widest} {
      min-height: 420px;
    }`};
  ${(props) =>
    props.boxSetImage &&
    props.logo &&
    `
      margin: auto;
      max-width: 1595px;
  `}
`;

const HeroCardContainerInner = styled.div`
  width: 100%;
`;

const HeroCardInner = styled.div`
  position: relative;
  padding: 20px;
  ${(props) =>
    props.noImage || props.noBoxsetImage
      ? ``
      : `
      display: grid;
      @media screen and (min-width: 440px) {
        grid-template-columns: 130px 1fr;
        column-gap: 12px;
      }
      @media screen and (min-width: 580px) {
        padding-top: 24px;
        grid-template-columns: 31% 1fr;
        grid-template-rows: 1fr auto;
      }
      @media screen and (min-width: 716px) {
        grid-template-columns: 200px 1fr;
      }
  `}

  ${(props) =>
    props.isProgramme &&
    `
      padding: 15px;
  `};
  @media ${device.tablet} {
    padding-left: 0;
    ${(props) =>
      props.isProgramme &&
      `
        padding: 25px;
    `};
  }
  @media ${device.desktop} {
    column-gap: 24px;
  }
  @media ${device.widest} {
    column-gap: 36px;
    ${(props) =>
      props.isProgramme &&
      `
        padding: 0 50px;
        top: 100%;
    `};
    ${(props) => props.isProgramme && !props.recommended && `padding: 50px;`};
  }
  @media screen and (min-width: 1920px) {
    ${(props) =>
      props.isCategory &&
      `
        margin: auto;
        max-width: 1595px;
    `}
    ${(props) =>
      props.isProgramme &&
      `
      padding: 50px;
    `};
  }
`;

const CategoryLogo = styled.img`
  max-width: 168px;
  margin: 15px;
  @media ${device.tablet} {
    margin: 12px 0 0;
    padding: 0;
  }
`;

const HeroTitleGroup = styled.div`
  padding-top: 12px;
  ${CategoryLogo} {
    margin: 0 0 2.8em;
    padding: 0;
  }
  @media screen and (min-width: 440px) {
    align-self: end;
  }
`;

const HeroCardImageContainer = styled.div`
  background-size: contain;
  ${(props) =>
    props.theme.name === 'light'
      ? `border: 1px solid ${props.theme.color.white5}`
      : `border: 1px solid ${props.theme.color.black5}`};

  border-radius: 12px;
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? `0px 0px 0px 1px ${props.theme.color.white15}`
      : ''};
  ${(props) =>
    props.theme.name === 'light' &&
    `background-image:url(${heroBoxSetMissingImageLight})`};
  ${(props) =>
    props.theme.name === 'dark' &&
    `background-image:url(${heroBoxSetMissingImageDark})`};

  width: 135px;

  @media screen and (min-width: 580px) {
    grid-row: 1 / 3;
    height: 0;
    padding-bottom: 150%;
    position: relative;
    width: 100%;
  }
`;

const HeroCardImage = styled.img`
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light'
        ? props.theme.color.white5
        : props.theme.color.black5};
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? `0px 0px 0px 1px ${rgba(props.theme.color.whiteFull, 0.15)}`
      : ''};

  border-radius: 12px;
  height: 100%;
  width: 100%;
  @media screen and (min-width: 580px) {
    position: absolute;
  }
`;

const HeroCardContent = styled.div`
  ${(props) => props.theme.fontBook};
  @media screen and (min-width: 440px) {
    padding-top: 12px;
    grid-column: 1 / -1;
  }
  @media screen and (min-width: 580px) {
    grid-column: 2;
  }
`;

const HeroCardCtas = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 12px;
  ${(props) => props.isProgramme && `flex-direction: column;`};
  @media screen and (min-width: 1024px) {
    ${(props) => props.isProgramme && `flex-direction: row;`};
  }
  div a {
    padding: 8px 26px;
    font-size: 14px;
    line-height: 1.4em;
    ${(props) => props.isProgramme && `line-height: 21px;`};
    @media ${device.widest} {
      padding: 14px 42px;
      font-size: 18px;
    }
  }
  div + div {
    margin-top: 1em;
    @media ${device.desktop} {
      margin-top: 0;
      margin-left: 1.4em;
    }
  }
`;

const HeroCardButtonContainer = styled.div`
  @media screen and (max-width: 1023px) {
    a {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
`;

const Tag = styled.h6`
  ${(props) => props.theme.font.subtitle2.bold};
  margin: 0;
  padding-bottom: 0.4em;
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.black90
      : props.theme.color.white90};
  ${(props) => props.isProgramme && `color: ${props.theme.color.primaryFull}`};
  text-shadow: 0px 0px 2px
    ${(props) =>
      props.theme.name === 'light'
        ? props.theme.color.white90
        : props.theme.color.blackFull};
  @media ${device.tablet} {
    ${(props) => props.theme.font.body.bold};
    line-height: 1.1em;
    letter-spacing: -0.025em;
  }
  @media ${device.desktop} {
    margin-bottom: 0;
  }
  @media ${device.widest} {
    ${(props) => props.theme.font.h5};
  }
`;

const Title = styled.h2`
  ${(props) => props.theme.font.h4};
  margin: 0;
  max-width: 20em;
  padding-bottom: 0.4em;
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.blackFull
      : props.theme.color.whiteFull};
  text-shadow: 0px 0px 4px
    ${(props) =>
      props.theme.name === 'light'
        ? props.theme.color.white90
        : props.theme.color.blackFull};
  ${(props) => props.isProgramme && `${props.theme.font.h6}`};
  @media screen and (min-width: 440px) {
    padding-bottom: 0;
  }
  @media ${device.tablet} {
    ${(props) => props.theme.font.h3};
    ${(props) =>
      props.isProgramme &&
      `
      ${props.theme.font.h5};
    `};
  }
  @media ${device.widest} {
    ${(props) => props.theme.font.h2};
    ${(props) =>
      props.isProgramme &&
      `
      ${props.theme.font.h3};
      max-width: 600px;
    `};
    ${(props) => !props.recommended && props.programmeNext && `width: 100%;`};
  }
`;

const Description = styled.p`
  margin: 0;
  max-width: 30em;
  font-size: 16px;
  line-height: 1.4em;
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.blackFull
      : props.theme.color.whiteFull} !important;
  ${(props) =>
    props.isProgramme &&
    `
      ${props.theme.font.subtitle2.book};
      color: ${
        props.theme.name === 'light'
          ? props.theme.color.black90
          : props.theme.color.white90
      } !important;
      letter-spacing: -0.1px;
  `};
  @media ${device.tablet} {
    font-size: 18px;
  }
  @media ${device.widest} {
    font-size: 20px;
    ${(props) =>
      props.isProgramme &&
      `
      padding: 0.25em 0px 0.5em;
      font-size: 34px;
      font-weight: bold;
      line-height: 40px;
      letter-spacing: -1px;
    `};
  }
  @media screen and (min-width: 1830px) {
    ${(props) =>
      props.isProgramme &&
      `
      padding: .5em 0 1em;
    `};
  }
`;
